import { c as _c } from "react/compiler-runtime";
import "client-only";
import { useJpmuid } from "@/hooks/tracking/jpmuid/use-jpmuid.hook";
import { useTracking } from "@/hooks/tracking/use-tracking";
import { useAuthentication } from "@/vendors/authentication/hooks/use-authentication.hook";
import { useCookiebotConsent } from "@/vendors/Cookiebot/_hooks/use-cookiebot-consent.hook";
import { useObservationBoundary } from "@/vendors/Snowplow/hooks/use-observation-boundary.hook";
import type { BoundaryType } from "@/vendors/Snowplow/types/observation-boundary-tracker.types";
type UseInViewObservationTrackingProps = {
  readonly boundaryId: string;
  readonly boundaryType: BoundaryType;
  readonly recommendationId?: string;
  readonly recommendationSignature?: string;
};

/**
 * High-level hook for tracking element visibility within a boundary.
 *
 * This is a facade that combines `useObservationBoundary` with the tracking
 * system, automatically emitting "in-view" events with user context.
 *
 * @param boundaryId - Unique identifier for this boundary instance
 * @param boundaryType - Category of the boundary (e.g., "recommended-list", "job-list")
 * @param recommendationId - Optional recommendation ID for personalized content
 * @param recommendationSignature - Optional signature for recommendation verification
 *
 * @returns Ref to attach to the boundary container element
 */
export function useInViewObservationTracking(t0) {
  const $ = _c(11);
  const {
    boundaryId,
    boundaryType,
    recommendationId,
    recommendationSignature
  } = t0;
  const {
    emit
  } = useTracking();
  const {
    jpmuid
  } = useJpmuid();
  const {
    user
  } = useAuthentication();
  const consent = useCookiebotConsent();
  let t1;
  if ($[0] !== consent || $[1] !== emit || $[2] !== jpmuid || $[3] !== recommendationId || $[4] !== recommendationSignature || $[5] !== user) {
    t1 = boundaryData => {
      emit({
        event: "in-view",
        model: {
          boundaryData,
          consent,
          jpmuid,
          recommendationId,
          recommendationSignature,
          user
        }
      });
    };
    $[0] = consent;
    $[1] = emit;
    $[2] = jpmuid;
    $[3] = recommendationId;
    $[4] = recommendationSignature;
    $[5] = user;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== boundaryId || $[8] !== boundaryType || $[9] !== t1) {
    t2 = {
      boundaryId,
      boundaryType,
      onBoundaryExit: t1
    };
    $[7] = boundaryId;
    $[8] = boundaryType;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const {
    boundaryRef
  } = useObservationBoundary(t2);
  return boundaryRef;
}